import React, { useState, useEffect } from 'react';
import { useSupportHours } from '../../hooks/useSupportHours';
import '../packages/package-dialogs.css';
import { PageSkeleton } from '../../../components/loading/skeletons';
import './SupportHoursConfig.css';

const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const CopyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const SupportHoursConfig = ({ onClose }) => {
  const { hours, loading, error, updateSupportHours } = useSupportHours();
  const [localHours, setLocalHours] = useState(hours);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    setLocalHours(hours);
  }, [hours]);

  const handleToggleDay = (day) => {
    const key = day.toLowerCase();
    const updatedHours = {
      ...localHours,
      [key]: {
        ...localHours[key],
        enabled: !localHours[key]?.enabled,
      },
    };
    setLocalHours(updatedHours);
  };

  const handleTimeChange = (day, field, value) => {
    const key = day.toLowerCase();
    const updatedHours = {
      ...localHours,
      [key]: {
        ...localHours[key],
        [field]: value,
      },
    };
    setLocalHours(updatedHours);
  };

  const copyToAllDays = (sourceDay) => {
    const sourceHours = localHours[sourceDay.toLowerCase()];
    const updatedHours = DAYS_OF_WEEK.reduce((acc, day) => ({
      ...acc,
      [day.toLowerCase()]: { ...sourceHours },
    }), {});
    setLocalHours(updatedHours);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const success = await updateSupportHours(localHours);
      if (success) {
        setSaveSuccess(true);
        setTimeout(() => {
          setSaveSuccess(false);
          onClose();
        }, 1500);
      }
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageSkeleton showHeader={false} variant="form" rows={4} label="Loading support hours" />;
  }

  if (error) {
    return (
      <div className="support-hours-config__alert support-hours-config__alert--error" role="alert">
        Error loading support hours: {error}
      </div>
    );
  }

  return (
    <div className="support-hours-config">
      {saveError && (
        <div className="support-hours-config__alert support-hours-config__alert--error" role="alert">
          Error saving changes: {saveError}
        </div>
      )}

      {saveSuccess && (
        <div className="support-hours-config__alert support-hours-config__alert--success" role="status">
          Support hours updated successfully!
        </div>
      )}

      <div className="support-hours-config__days">
        {DAYS_OF_WEEK.map((day) => {
          const dayKey = day.toLowerCase();
          const dayHours = localHours[dayKey];
          const enabled = dayHours?.enabled ?? false;

          return (
            <div
              key={day}
              className={`support-hours-config__day ${enabled ? '' : 'support-hours-config__day--disabled'}`}
            >
              {saving && (
                <div className="support-hours-config__overlay">
                  <div className="support-hours-config__spinner" aria-hidden="true" />
                </div>
              )}

              <label
                className="pkg-dialog-check support-hours-config__day-label"
                htmlFor={`support-hours-${dayKey}`}
              >
                <input
                  id={`support-hours-${dayKey}`}
                  type="checkbox"
                  checked={enabled}
                  onChange={() => handleToggleDay(day)}
                />
                {day}
              </label>

              <input
                type="time"
                className="support-hours-config__time-input"
                value={dayHours?.start ?? '09:00'}
                onChange={(e) => handleTimeChange(day, 'start', e.target.value)}
                disabled={!enabled}
                step={300}
                aria-label={`${day} start time`}
              />

              <span className="support-hours-config__separator">to</span>

              <input
                type="time"
                className="support-hours-config__time-input"
                value={dayHours?.end ?? '17:00'}
                onChange={(e) => handleTimeChange(day, 'end', e.target.value)}
                disabled={!enabled}
                step={300}
                aria-label={`${day} end time`}
              />

              <button
                type="button"
                className="support-hours-config__copy"
                onClick={() => copyToAllDays(day)}
                title="Copy these hours to all days"
                aria-label={`Copy ${day} hours to all days`}
              >
                <CopyIcon />
              </button>
            </div>
          );
        })}
      </div>

      <footer className="support-hours-config__footer">
        <button type="button" className="pkg-btn pkg-btn-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="pkg-btn pkg-btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </footer>
    </div>
  );
};

export default SupportHoursConfig;
